import {axiosInstance as client} from "./axiosInstance";
import {createPage} from "../../helpers/paginatedSearchHelpers";
import type {Page, PagedResponse} from "../../helpers/paginatedSearchHelpers";
import type User from "../users/User";

export type NotificationRule = {
	name: string;
};

export type NotificationRuleDefinition = {
	name: string;
	audience: "students" | "no_one";
	category: "study";
};

export type SentNotificationStatistics = {
	notificationName: string;
	sentDate: string;
	numberOfRecipients: number;
};

type UserNotification = {
	id: string;
	name: string;
	category: string;
	timestamp: string;
};

export type CourseNotificationSearchResult = UserNotification & {
	recipientId: number;
};

const notificationsService = {
	getAvailableNotificationRules: async (): Promise<
		NotificationRuleDefinition[]
	> => {
		const {data} = await client.get("/api/notification-rules");
		return data.content;
	},

	getCourseNotificationRules: async (
		courseId: number
	): Promise<NotificationRule[]> => {
		const {data} = await client.get(
			`/api/courses/${courseId}/notification-rules`
		);

		return data.content;
	},

	getSentNotificationStatistics: async (
		courseId: number,
		timeZone: string,
		criteria: {sentAfter: string},
		pageSize: number
	): Promise<Page<SentNotificationStatistics, string>> => {
		const params = new URLSearchParams();

		params.append("pageSize", pageSize.toString());
		params.append("sort", "-sentDate");

		params.append("timeZone", timeZone);

		params.append("sentAfter", criteria.sentAfter);

		const {data} = await client.get(
			`/api/courses/${courseId}/notification-statistics`,
			{
				params,
			}
		);

		return createPage(client, data);
	},

	getUserCourseNotifications: async (
		userId: number,
		courseId: number
	): Promise<UserNotification[]> => {
		const {data} = await client.get(
			`/api/users/${userId}/notifications?category=study&courseId=${courseId}&sort=-timestamp`
		);

		return data.content;
	},

	searchCourseNotifications: async (
		courseId: number,
		criteria: {name?: string; sentAfter?: string; sentBefore?: string}
	): Promise<
		Page<CourseNotificationSearchResult, string, {recipient: Map<number, User>}>
	> => {
		const params = new URLSearchParams();

		params.append("sort", "-timestamp");

		if (criteria.name) {
			params.append("name", criteria.name);
		}
		if (criteria.sentAfter) {
			params.append("sentAfter", criteria.sentAfter);
		}
		if (criteria.sentBefore) {
			params.append("sentBefore", criteria.sentBefore);
		}

		const {data} = await client.get<
			PagedResponse<CourseNotificationSearchResult, {recipient: User[]}>
		>(`/api/courses/${courseId}/notifications`, {
			params,
		});

		return createPage(client, data, {
			preprocessRelated: (related) => ({
				recipient: related.recipient.reduce(
					(m, r) => m.set(r.id, r),
					new Map<number, User>()
				),
			}),
		});
	},

	updateCourseNotificationRules: async (
		courseId: number,
		rules: NotificationRule[]
	): Promise<void> => {
		await client.put(`/api/courses/${courseId}/notification-rules`, rules);
	},
};

export default notificationsService;
